// =============================================================================
// MeuDriver — SkillRatingForm
// Formulário de avaliação de 10 habilidades (sliders 0-5) para instrutores
// Aparece após marcar aula como concluída em InstructorLessons
// =============================================================================

import { useState, useEffect } from 'react'
import { Sliders, Save, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'

const SKILLS = [
  { key: 'skill_partida',       label: 'Partida e parada' },
  { key: 'skill_direcao',       label: 'Direção' },
  { key: 'skill_freios',        label: 'Freios' },
  { key: 'skill_marchas',       label: 'Marchas' },
  { key: 'skill_baliza',        label: 'Baliza' },
  { key: 'skill_garagem',       label: 'Garagem' },
  { key: 'skill_retorno',       label: 'Retorno / rotatória' },
  { key: 'skill_via_rapida',    label: 'Via rápida' },
  { key: 'skill_noturna',       label: 'Direção noturna' },
  { key: 'skill_comportamento', label: 'Comportamento geral' },
] as const

type SkillKey = (typeof SKILLS)[number]['key']

type SkillValues = Record<SkillKey, number>

const DEFAULT_VALUES: SkillValues = {
  skill_partida: 3,
  skill_direcao: 3,
  skill_freios: 3,
  skill_marchas: 3,
  skill_baliza: 3,
  skill_garagem: 3,
  skill_retorno: 3,
  skill_via_rapida: 3,
  skill_noturna: 3,
  skill_comportamento: 3,
}

const LEVEL_COLORS = ['bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-teal-400', 'bg-green-500']
const LEVEL_LABELS = ['', 'Ruim', 'Regular', 'Bom', 'Muito bom', 'Excelente']

function SkillSlider({
  label,
  skillKey,
  value,
  onChange,
}: {
  label: string
  skillKey: SkillKey
  value: number
  onChange: (key: SkillKey, v: number) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <label className="text-gray-700 font-medium">{label}</label>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{LEVEL_LABELS[value]}</span>
          <div className={cn('h-5 w-5 rounded-full flex items-center justify-center text-white text-xs font-bold', LEVEL_COLORS[value - 1] ?? 'bg-gray-200')}>
            {value}
          </div>
        </div>
      </div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(skillKey, Number(e.target.value))}
        className="w-full h-2 rounded-full accent-teal-600 cursor-pointer"
        data-testid={`skill-slider-${skillKey}`}
      />
      <div className="flex justify-between text-[10px] text-muted-foreground px-0.5">
        <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
      </div>
    </div>
  )
}

interface Props {
  bookingId: string
  studentId: string
  studentName: string
  onDone?: () => void
}

export function SkillRatingForm({ bookingId, studentId, studentName, onDone }: Props) {
  const { user } = useAuth()
  const [values, setValues] = useState<SkillValues>(DEFAULT_VALUES)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [existingId, setExistingId] = useState<string | null>(null)

  // Carrega avaliação existente, se houver
  useEffect(() => {
    supabase
      .from('lesson_skill_ratings')
      .select('*')
      .eq('booking_id', bookingId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return
        setExistingId(data.id)
        const v: Partial<SkillValues> = {}
        for (const { key } of SKILLS) {
          v[key] = (data as any)[key] ?? 3
        }
        setValues({ ...DEFAULT_VALUES, ...v })
        setNotes(data.notes ?? '')
      })
  }, [bookingId])

  function handleChange(key: SkillKey, val: number) {
    setValues((prev) => ({ ...prev, [key]: val }))
  }

  async function handleSave() {
    if (!user) return
    setSaving(true)
    try {
      const payload = {
        booking_id: bookingId,
        instructor_id: user.id,
        student_id: studentId,
        notes: notes.trim() || null,
        ...values,
      }

      if (existingId) {
        await supabase
          .from('lesson_skill_ratings')
          .update(payload)
          .eq('id', existingId)
          .throwOnError()
      } else {
        const { data } = await supabase
          .from('lesson_skill_ratings')
          .insert(payload)
          .select('id')
          .single()
        if (data) setExistingId(data.id)
      }

      setSaved(true)
      toast.success('Avaliação de habilidades salva!')
      setTimeout(() => setSaved(false), 2500)
      onDone?.()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao salvar avaliação.')
    } finally {
      setSaving(false)
    }
  }

  const avg = (Object.values(values).reduce((a, b) => a + b, 0) / SKILLS.length).toFixed(1)

  return (
    <div className="mt-2 border-t pt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-teal-700 transition-colors"
        data-testid="skill-rating-toggle"
      >
        <Sliders className="h-3.5 w-3.5" />
        {existingId ? 'Editar avaliação de habilidades' : 'Avaliar habilidades de direção'}
        {existingId && <span className="ml-1 rounded-full bg-teal-100 px-1.5 text-[10px] text-teal-700">salvo</span>}
      </button>

      {expanded && (
        <div className="mt-3 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">
              Avaliando: <span className="text-teal-700">{studentName}</span>
            </p>
            <div className="flex items-center gap-1.5 rounded-full bg-teal-50 border border-teal-200 px-3 py-1">
              <span className="text-xs text-muted-foreground">Média:</span>
              <span className="text-sm font-bold text-teal-700">{avg}</span>
              <span className="text-xs text-muted-foreground">/ 5</span>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {SKILLS.map(({ key, label }) => (
              <SkillSlider
                key={key}
                label={label}
                skillKey={key}
                value={values[key]}
                onChange={handleChange}
              />
            ))}
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-600">Observações (opcional)</label>
            <textarea
              rows={2}
              maxLength={500}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex.: Melhorou muito no estacionamento, mas ainda precisa trabalhar as marchas."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-teal-400"
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-600 hover:bg-teal-700 gap-1.5"
              data-testid="skill-rating-save"
            >
              {saved
                ? <><Check className="h-3.5 w-3.5" /> Salvo!</>
                : <><Save className="h-3.5 w-3.5" /> {existingId ? 'Atualizar' : 'Salvar'} avaliação</>}
            </Button>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-xs text-muted-foreground hover:text-gray-700"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
